import React, { useState } from "react";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { Loader2, CheckCircle2, Building2 } from "lucide-react";
import { B2B_BENEFITS, waLink } from "@/data/site";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const inputCls = "w-full bg-white/90 border border-white/30 rounded-2xl px-5 py-3 focus-visible:ring-2 focus-visible:ring-turq-light outline-none text-navy h-auto";

export default function Hospitals() {
  const [form, setForm] = useState({ organization: "", contact_person: "", phone: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.organization || !form.contact_person || !form.phone) { toast.error("Please fill organization, contact person and phone."); return; }
    setLoading(true);
    try { await api.post("/partnerships", form); setDone(true); toast.success("Partnership request sent! Our team will reach out."); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const scrollForm = () => document.getElementById("partnership-form")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="hospitals" className="py-20 md:py-28 bg-navy relative overflow-hidden" data-testid="hospitals-section">
      <div className="absolute -top-24 -right-24 w-96 h-96 bg-turq/15 rounded-full blur-3xl" />
      <div className="max-w-7xl mx-auto px-5 md:px-10 relative grid lg:grid-cols-2 gap-12 items-start">
        <div>
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-turq-light">For Hospitals & Clinics</span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white mt-3 tracking-tight">Partner With DOABA Diagnostics</h2>
          <p className="text-white/70 mt-5 text-lg leading-relaxed">
            Hospitals, clinics, doctors and healthcare centres can outsource their laboratory testing to DOABA Diagnostics. Reliable, accurate and affordable diagnostics backed by a dedicated support team.
          </p>
          <div className="mt-7 grid sm:grid-cols-2 gap-3">
            {B2B_BENEFITS.map((b) => {
              const Icon = Icons[b.icon] || Building2;
              return (
                <div key={b.title} className="flex items-center gap-3 bg-white/10 backdrop-blur-xl rounded-2xl p-4 border border-white/15" data-testid={`b2b-${b.title}`}>
                  <span className="w-10 h-10 rounded-xl bg-turq/20 flex items-center justify-center text-turq-light shrink-0"><Icon className="w-5 h-5" strokeWidth={1.6} /></span>
                  <span className="text-sm font-medium text-white">{b.title}</span>
                </div>
              );
            })}
          </div>
          <Button onClick={scrollForm} className="mt-8 rounded-full bg-turq hover:bg-turq-dark text-white px-8 py-6 text-base shadow-[0_10px_30px_rgba(0,180,216,0.3)]" data-testid="b2b-request-btn">
            Request a Partnership
          </Button>
        </div>

        <motion.div id="partnership-form" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="bg-white/10 backdrop-blur-2xl rounded-[2rem] p-7 md:p-9 border border-white/20">
          {done ? (
            <div className="text-center py-10" data-testid="b2b-success">
              <CheckCircle2 className="w-16 h-16 text-turq-light mx-auto mb-4" strokeWidth={1.5} />
              <h3 className="text-xl font-heading text-white">Request Received!</h3>
              <p className="text-white/70 mt-2">Thank you. Our partnerships team will contact you shortly.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="b2b-form">
              <h3 className="font-heading text-xl font-semibold text-white">Partnership Enquiry</h3>
              <div><Label className="text-white text-sm mb-1.5 block">Hospital / Clinic Name *</Label><Input className={inputCls} value={form.organization} onChange={set("organization")} data-testid="b2b-org-input" /></div>
              <div><Label className="text-white text-sm mb-1.5 block">Contact Person *</Label><Input className={inputCls} value={form.contact_person} onChange={set("contact_person")} data-testid="b2b-person-input" /></div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label className="text-white text-sm mb-1.5 block">Phone *</Label><Input className={inputCls} value={form.phone} onChange={set("phone")} data-testid="b2b-phone-input" /></div>
                <div><Label className="text-white text-sm mb-1.5 block">Email</Label><Input className={inputCls} type="email" value={form.email} onChange={set("email")} data-testid="b2b-email-input" /></div>
              </div>
              <div><Label className="text-white text-sm mb-1.5 block">Message</Label><Textarea className={inputCls} rows={3} value={form.message} onChange={set("message")} data-testid="b2b-message-input" /></div>
              <Button type="submit" disabled={loading} className="w-full rounded-full bg-turq hover:bg-turq-dark text-white py-6 text-base" data-testid="b2b-submit-btn">
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</> : "Send Partnership Request"}
              </Button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
